import { Bell, BellOff, BotOff, Check, Clock, ListChecks, Lock, LogOut, Mail, MailOpen, Trash2, Users, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  formatPendingUntilShort,
  formatWaitShort,
  getThreadDisplayName,
  getThreadSecondaryIdentity,
  getThreadInitials,
  getThreadPreview,
  getThreadTime,
  getWhatsAppInboxWaitingLabel,
  getWhatsAppInboxWaitingMinutes,
  isInternalDirectThread,
  TIER_STYLES,
  waitTier,
} from "@/lib/messages/messages-helpers";
import { getAvatarColorClasses } from "@/lib/messages/avatar-color";
import { getWaGroupInfo, isWaGroupThread } from "@/lib/messages/wa-groups";
import { getMergedGroupSources, mergedGroupUnreadTotal } from "@/lib/messages/merged-wa-groups";
import { tenantColorVars } from "@/lib/messages/merged-thread-colors";

// Internal-chat context-menu row styles (full-width action rows, matching the menu's
// compact typography).
// Stable identity so the unmerged path never allocates a new array per render.
const EMPTY_SOURCES = Object.freeze([]);

const CHAT_MENU_ROW_CLASS =
  "flex w-full items-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-left text-[10.5px] font-medium leading-none text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const CHAT_MENU_DESTRUCTIVE_ROW_CLASS =
  "flex w-full items-center gap-1.5 rounded-md border border-destructive/40 px-2 py-1 text-left text-[10.5px] font-medium leading-none text-destructive transition hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
import {
  getThreadFlagCatalogMenuSections,
  getThreadFlagStrings,
  getThreadLockReason,
  humanizeLockReason,
} from "@/lib/thread-flags";
import { getPatientTagStrings, PATIENT_TAG_CATALOG } from "@/lib/patient-tags";
import {
  getEffectiveThreadStatus,
  getThreadPendingUntilMs,
  getThreadStatusDefinition,
  THREAD_STATUS_CATALOG,
  THREAD_STATUSES,
} from "@/lib/thread-status";
import { findGroupById, getGroupColor, getThreadGroupId } from "@/lib/thread-groups";
import { cn } from "@/lib/utils";
import { ThreadPreviewMarkdown } from "@/components/messages/thread-preview-markdown";
import { ThreadGroupChip } from "@/components/messages/thread-group-chip";
import { HealoChannelGlyph } from "@/components/messages/healo-channel-glyph";
import PresenceDot from "@/components/presence/presence-dot";
import { PatientTagStrip } from "@/components/messages/patient-tag-strip";
// DISABLED (for now): thread assignment — assignment lives on tasks only.
// import { ClaimButton } from "@/components/messages/claim-button";
// import { getThreadAssignee } from "@/lib/thread-assignee";
import { getAuthUser } from "@/lib/api/token-store";
import { Fragment, memo } from "react";

// The flag context-menu catalog is static config — compute the menu sections ONCE for the whole
// module rather than once per row (this ran ~N times per list render before).
const THREAD_FLAG_MENU_SECTIONS = getThreadFlagCatalogMenuSections();

/** WhatsApp mark — simplified glyph from handoff design (green fill, no monochrome currentColor). */
function WhatsappThreadGlyph({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="#22c55e" className={className} aria-hidden focusable="false">
      <path d="M12 2a10 10 0 0 0-8.5 15.2L2 22l4.9-1.4A10 10 0 1 0 12 2zm5.3 14.1c-.2.6-1.3 1.2-1.8 1.2-.5.1-1 .1-1.6-.1-.4-.1-.9-.3-1.5-.6-2.7-1.2-4.4-3.9-4.6-4.1-.1-.2-1.1-1.4-1.1-2.7 0-1.3.7-1.9.9-2.2.2-.2.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.8 2c.1.2.1.4 0 .5-.1.2-.2.3-.3.4l-.4.4c-.1.1-.3.3-.1.5.2.3.8 1.2 1.7 2 1.1 1 2 1.3 2.3 1.4.3.1.4.1.6-.1.2-.2.7-.8.9-1.1.2-.3.4-.2.6-.1.3.1 1.8.9 2.1 1 .3.2.5.2.6.4.1.2.1.8-.1 1.4z" />
    </svg>
  );
}

/** Initials for the 18px owner avatar from the assignee's display name. */
function ownerInitials(name) {
  const tokens = String(name || "")
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return "?";
  const first = tokens[0][0]?.toUpperCase() || "";
  if (tokens.length === 1) return first;
  return first + (tokens[tokens.length - 1][0]?.toUpperCase() || "");
}

/**
 * The compact 18px owner avatar shown on claimed rows — the whole team sees who has it, so
 * no two people answer the same patient. Indigo = you; amber ring = bot auto-assignment.
 */
export function ThreadOwnerBadge({ assignee, isMe, className }) {
  if (!assignee) return null;
  const label = isMe ? "Owned by you" : `Owned by ${assignee.name}`;
  const title = assignee.type === "auto" ? `${label} (auto-assigned)` : label;
  return (
    <span
      className={cn(
        "inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full text-[9px] font-bold leading-none",
        isMe
          ? "bg-indigo-600 text-white dark:bg-indigo-500"
          : "bg-muted text-muted-foreground",
        assignee.type === "auto" ? "ring-1 ring-amber-400 dark:ring-amber-500" : "",
        className,
      )}
      title={title}
      aria-label={title}
    >
      {ownerInitials(assignee.name)}
    </span>
  );
}

/**
 * "AI paused" cue: violet while locked, emerald while staff-handles. `iconOnly` renders a
 * tiny 18px glyph tile (used on list rows, where the label on every paused row was noise);
 * the labeled pill remains for roomier surfaces (board cards, banners).
 */
export function AiPausedChip({ locked, iconOnly = false, className }) {
  const tone = locked
    ? "border-violet-200 bg-violet-50 text-violet-600 dark:border-violet-500/40 dark:bg-violet-950/60 dark:text-violet-300"
    : "border-emerald-200 bg-emerald-50 text-emerald-600 dark:border-emerald-500/40 dark:bg-emerald-950/60 dark:text-emerald-300";
  const title = locked ? "AI paused — conversation locked" : "AI paused — staff handles replies";
  if (iconOnly) {
    return (
      <span
        className={cn(
          "inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-md border",
          tone,
          className,
        )}
        title={title}
        aria-label={title}
      >
        <BotOff className="size-2.5!" strokeWidth={2.25} aria-hidden />
      </span>
    );
  }
  return (
    <span
      className={cn(
        "inline-flex h-[18px] shrink-0 items-center gap-1 rounded-md border px-1.5 text-[10px] font-medium leading-none",
        tone,
        className,
      )}
      title={title}
    >
      <BotOff className="size-2.5! shrink-0" strokeWidth={2.25} aria-hidden />
      AI paused
    </span>
  );
}

/** "done/total" checklist badge: indigo while work remains, emerald once everything's done. */
export function TaskCountBadge({ open, total, className }) {
  if (!Number.isFinite(total) || total <= 0) return null;
  const done = Math.max(0, total - (Number(open) || 0));
  const complete = done >= total;
  return (
    <span
      className={cn(
        "inline-flex h-[18px] shrink-0 items-center gap-1 rounded-md border px-1.5 font-mono text-[10px] font-medium leading-none",
        complete
          ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-950/60 dark:text-emerald-300"
          : "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/40 dark:bg-indigo-950/60 dark:text-indigo-300",
        className,
      )}
      title={complete ? "All tasks done" : `${total - done} open task${total - done === 1 ? "" : "s"}`}
    >
      <ListChecks className="size-2.5! shrink-0" strokeWidth={2.25} aria-hidden />
      {done}/{total}
    </span>
  );
}

/**
 * @param {object} props
 * @param {object} props.thread
 * @param {number} props.index
 * @param {number} [props.listClockMs]
 * @param {"all"|"needs"|"mine"} [props.lens] — active list lens; wait chips render only in "needs" (chronological time elsewhere)
 * @param {number | null} [props.botAckMinutes] elapsed minutes when this row is in the "Unanswered bot replies" section (drives the amber chip)
 * @param {number | null} [props.patientReplyMinutes] elapsed minutes when this row is in the "Unanswered patient reply" section (drives the rose chip)
 * @param {{ directUserIds?: string[], teamUserIds?: string[], botIds?: string[] } | null | undefined} props.threadMembership
 * @param {string | null} props.selectedThreadId
 * @param {Record<string, boolean>} props.localUnreadByThreadId
 * @param {(threadId: string) => void} props.onSelectThread
 * @param {(threadId: string, shortName: string, checked: boolean) => void} props.onThreadFlagChange
 * @param {Array<{ id: string, name: string, colorId: string }>} [props.groups]
 * @param {(threadId: string, groupId: string | null) => void} [props.onThreadGroupChange]
 * @param {(threadId: string) => void} [props.onClaimThread] — DISABLED (for now): hover quick-action on open rows
 * @param {(threadId: string, tagKey: string, checked: boolean) => void} [props.onPatientTagToggle]
 * @param {(threadId: string, status: string) => void} [props.onStatusChange] — context-menu status override ("" clears)
 */
const ThreadListItemImpl = function ThreadListItem({
  thread,
  index = 0,
  listClockMs,
  lens = "all",
  botAckMinutes = null,
  patientReplyMinutes = null,
  // Hoisted from getAuthUser() by the parent list so it isn't parsed once per row; falls back to a
  // local lookup when the row is rendered without it.
  currentUserId: currentUserIdProp = null,
  threadMembership,
  selectedThreadId,
  localUnreadByThreadId,
  onSelectThread,
  onThreadFlagChange,
  groups = [],
  onThreadGroupChange,
  // DISABLED (for now): thread claim — assignment lives on tasks only.
  // onClaimThread,
  onPatientTagToggle,
  onStatusChange,
  // DEV-ONLY admin action (see messages-page): opens the delete-thread confirmation.
  onDeleteThread,
  // Internal Chats row actions (undefined in Messages scope).
  onMarkThreadRead,
  onMarkThreadUnread,
  onToggleMute,
  onLeaveThread,
  onDeleteChat,
  mutedThreadIds = null,
  onlineUserIds = null,
  availabilityByUserId = null,
  typingThreadIds = null,
  isInternalScope = false,
  showWhatsappNumber = false,
  // True for rows rendered INSIDE a group section (groups view) — the section header already
  // names the group, so the per-row chip would just repeat it.
  hideGroupChip = false,
}) {
  const tid = String(thread?.threadId || thread?.id || "").trim();
  const rowKey = thread.threadId || thread.id || tid;
  // Merged group rows roll their siblings up (as a MAX, never a sum — the same WhatsApp
  // message lands in every gateway's copy). The adapter returns the row's own count for any
  // unmerged row, so this is a no-op everywhere else.
  const unreadCount = isInternalScope
    ? Number(thread?.unreadCount ?? thread?.unread_count ?? 0)
    : mergedGroupUnreadTotal(thread);
  const hasServerUnreadCount = Number.isFinite(unreadCount) && unreadCount > 0;
  const hasLocalUnread = Boolean(tid && localUnreadByThreadId[tid]);
  const showUnreadBadge = hasServerUnreadCount || hasLocalUnread;
  const clock = listClockMs ?? Date.now();
  const flagStrings = getThreadFlagStrings(thread);
  const flagSet = new Set(flagStrings);
  const isLocked = flagSet.has("locked");
  // DISABLED (for now): staff_handles is retired and no longer counts as paused.
  // const isStaffHandles = flagSet.has("staff_handles");
  const aiPaused = isLocked /* || isStaffHandles */;
  const lockReason = isLocked ? getThreadLockReason(thread) : "";
  const waitMinutes = getWhatsAppInboxWaitingMinutes(thread, threadMembership ?? null, clock);
  const waitAriaLabel = getWhatsAppInboxWaitingLabel(thread, threadMembership ?? null, clock);
  const tier = waitTier(waitMinutes);
  const tierStyles = TIER_STYLES[tier];
  const timeLabel = getThreadTime(thread);
  // Wait/bot-ack urgency chips belong to the "Needs you" triage lens; the chronological lenses
  // stay calm and show plain times instead.
  const showWaitChip = lens === "needs" && waitMinutes != null;
  const showPatientReplyChip = lens === "needs" && patientReplyMinutes != null;
  const showBotAckChip = lens === "needs" && botAckMinutes != null;
  const { label: threadTitle, isPlaceholder: threadTitleIsPlaceholder } = getThreadDisplayName(thread, {
    isInternal: isInternalScope,
  });
  // The other identity fact (push name next to a phone title, phone next to a push-name
  // title) — a muted suffix so unresolved WhatsApp contacts keep both facts visible.
  const threadSecondaryIdentity = getThreadSecondaryIdentity(thread, { isInternal: isInternalScope });
  const currentGroupId = getThreadGroupId(thread);
  const currentGroup = currentGroupId ? findGroupById(groups, currentGroupId) : null;
  // DISABLED (for now): the owner badge — thread assignment is hidden; assignment
  // lives on tasks only.
  // const assignee = getThreadAssignee(thread);
  const currentUserId =
    currentUserIdProp != null ? currentUserIdProp : String(getAuthUser()?.id || "").trim();
  // const assignedToMe = Boolean(assignee && currentUserId && assignee.id === currentUserId);
  const isOptimistic = Boolean(thread.__optimistic);
  const isWhatsappThread = thread?.metadata?.conversationType === "whatsapp";
  // Patient mobile-app conversations (created via POST /api/threads/app) — the Healo glyph
  // is their channel badge, exactly like the WhatsApp mark above.
  const isAppThread =
    String(thread?.metadata?.conversationType || "").toLowerCase() === "app";
  // WhatsApp GROUP chat (the Groups lens). Group rows have no patient — the patient-tag and
  // workflow-status affordances below hide for them — and their identity is the group itself:
  // a Users-glyph avatar fallback plus a participant-count chip on the meta line.
  const isWaGroup = !isInternalScope && isWaGroupThread(thread);
  const waGroupParticipantCount = isWaGroup ? getWaGroupInfo(thread).participantCount : 0;
  const showWaGroupCountChip = isWaGroup && waGroupParticipantCount > 0;
  // Merged Groups view: this one row stands for the same WhatsApp group on several clinic
  // gateways. Falls back to a single synthetic source when unmerged, so length > 1 is false.
  const waGroupSources = isWaGroup ? getMergedGroupSources(thread) : EMPTY_SOURCES;
  const isMergedGroupRow = waGroupSources.length > 1;
  // Which business number this thread is on. Shown only when several numbers are configured and the
  // list isn't already filtered to one (showWhatsappNumber), so staff can tell the lines apart.
  // Group threads live on exactly one number too — their rows carry the same badge.
  const whatsappNumberLabel = String(thread?.whatsappNumberLabel || "").trim();
  const showNumberBadge =
    showWhatsappNumber && (isWhatsappThread || isWaGroup) && Boolean(whatsappNumberLabel);
  const isSelected = selectedThreadId === thread.threadId;
  const isEven = index % 2 === 0;
  // Workflow status drives the 3px left rail + avatar status dot (Messages scope only; Chats
  // keeps its presence dot). null = bot answering: no rail, no dot, nothing to triage.
  const effectiveStatus = isInternalScope ? null : getEffectiveThreadStatus(thread, clock);
  const statusDef = effectiveStatus ? getThreadStatusDefinition(effectiveStatus) : null;
  const pendingUntilMs = effectiveStatus === "pending" ? getThreadPendingUntilMs(thread) : null;
  const pendingUntilLabel =
    pendingUntilMs != null ? formatPendingUntilShort(thread?.metadata?.pendingUntil, clock) : null;
  const patientTags = isInternalScope ? [] : getPatientTagStrings(thread);
  const tasksOpen = Number(thread?.tasksOpen) || 0;
  const tasksTotal = Number(thread?.tasksTotal) || 0;
  // DISABLED (for now): the hover Claim quick-action — thread assignment is hidden.
  // const canClaim = Boolean(onClaimThread && effectiveStatus === "open" && !isInternalScope);
  // The avatar represents the row's named entity. For a 1:1 chat that's the other person
  // (thread.title / member name) — not the generic stored thread name like "Direct Chat". Groups
  // and others keep the stored name (or the placeholder label when the thread is unnamed). Using
  // the person's name also gives each 1:1 a distinct avatar colour.
  const isDirectThread = String(thread?.type || "").toLowerCase() === "direct";
  const avatarName = isDirectThread
    ? threadTitle || thread?.name || ""
    : threadTitleIsPlaceholder
      ? threadTitle
      : thread?.name || threadTitle || "";
  const avatarSeed = avatarName || threadTitle || getThreadInitials(threadTitle);
  const avatarColorClasses = getAvatarColorClasses(avatarSeed);
  // Green presence dot for 1:1 chats: the other member currently has the app open. Fed only in
  // the Chats view (onlineUserIds is null in Messages, so the dot never shows there).
  const presenceSet = onlineUserIds instanceof Set ? onlineUserIds : null;
  const otherDirectMemberId =
    isDirectThread && Array.isArray(thread?.userIds)
      ? thread.userIds.map((id) => String(id || "").trim()).find((id) => id && id !== currentUserId)
      : "";
  const showOnlineDot = Boolean(presenceSet && otherDirectMemberId && presenceSet.has(otherDirectMemberId));
  const { primary: primaryFlagDefs, rest: restFlagDefs } = THREAD_FLAG_MENU_SECTIONS;
  const unreadLabel = unreadCount > 99 ? "99+" : String(unreadCount);
  const unreadTitle = hasServerUnreadCount
    ? `Unread messages (${unreadLabel})`
    : "New unread message";

  const isMutedChat = Boolean(mutedThreadIds instanceof Set && tid && mutedThreadIds.has(tid));
  const showGroupChip = Boolean(currentGroup) && !hideGroupChip;
  const hasMetaRow =
    pendingUntilLabel ||
    lockReason ||
    patientTags.length > 0 ||
    showNumberBadge ||
    showWaGroupCountChip ||
    showGroupChip;

  const rowButton = (
    <Button
      type="button"
      variant="ghost"
      className={cn(
        "relative h-auto w-full justify-start gap-2.5 overflow-hidden rounded-none px-3 py-2 text-left transition-colors",
        isSelected
          ? "bg-accent/80 hover:bg-accent/90"
          : isEven
            ? "bg-transparent hover:bg-muted/60"
            : "bg-muted/60 hover:bg-muted/80 dark:bg-muted/35 dark:hover:bg-muted/50",
      )}
      onClick={() => onSelectThread(thread.threadId)}
    >
      {statusDef?.railClassName ? (
        <span
          className={cn(
            "absolute bottom-2 left-0 top-2 w-[3px] rounded-r-[3px]",
            statusDef.railClassName,
          )}
          title={statusDef.label}
          aria-hidden
        />
      ) : null}
      <div className="relative shrink-0">
        <Avatar className="h-10 w-10">
          <AvatarImage src={thread?.avatarUrl} />
          <AvatarFallback className={cn("text-xs font-semibold", avatarColorClasses)}>
            {/* Group subjects aren't people — initials would masquerade as a contact. */}
            {isWaGroup ? (
              <Users className="h-4 w-4" strokeWidth={2.25} aria-hidden />
            ) : (
              getThreadInitials(avatarName)
            )}
          </AvatarFallback>
        </Avatar>
        {statusDef ? (
          <span
            className={cn(
              "absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-background",
              statusDef.dotClassName,
            )}
            title={statusDef.label}
            aria-label={`Status: ${statusDef.label}`}
          />
        ) : showOnlineDot ? (
          <PresenceDot
            online
            availability={availabilityByUserId?.[otherDirectMemberId]}
            className="absolute bottom-0 right-0 h-3 w-3"
          />
        ) : null}
        {showUnreadBadge ? (
          hasServerUnreadCount ? (
            <span
              className="absolute -right-1 -top-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-primary-foreground ring-2 ring-background"
              title={unreadTitle}
              aria-label={unreadTitle}
            >
              {unreadLabel}
            </span>
          ) : (
            <span
              className="absolute -right-1 -top-1 inline-flex h-[12px] w-[12px] rounded-full bg-primary ring-2 ring-background"
              title={unreadTitle}
              aria-label={unreadTitle}
            />
          )
        ) : null}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            {isWhatsappThread ? (
              <span className="inline-flex shrink-0" title="WhatsApp">
                <WhatsappThreadGlyph className="size-3!" />
              </span>
            ) : isAppThread ? (
              <span className="inline-flex shrink-0" title="Healo app">
                <HealoChannelGlyph className="size-3!" />
              </span>
            ) : null}
            <span
              className={cn(
                "min-w-0 truncate text-sm font-medium",
                threadTitleIsPlaceholder ? "text-muted-foreground" : "text-foreground",
              )}
            >
              {threadTitle}
            </span>
            {threadSecondaryIdentity ? (
              <span
                className="min-w-0 shrink-[2] truncate text-xs font-normal text-muted-foreground"
                title={threadSecondaryIdentity}
              >
                ({threadSecondaryIdentity})
              </span>
            ) : null}
          </div>
          {showWaitChip ? (
            <span
              className={cn(
                "inline-flex shrink-0 items-center gap-1 rounded-[4px] px-[7px] py-[3px] font-mono text-[11px] font-semibold leading-none",
                tierStyles.chip,
              )}
              title={waitAriaLabel ?? undefined}
              aria-label={waitAriaLabel ?? undefined}
            >
              <span className={cn("h-[5px] w-[5px] rounded-full", tierStyles.dot)} />
              {formatWaitShort(waitMinutes)}
            </span>
          ) : showPatientReplyChip ? (
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded-[4px] border border-rose-300 bg-rose-100 px-[7px] py-[3px] font-mono text-[11px] font-semibold leading-none text-rose-900 dark:border-rose-500/40 dark:bg-rose-950/60 dark:text-rose-100"
              title="Unanswered patient reply"
              aria-label={`Unanswered patient reply, ${formatWaitShort(patientReplyMinutes)}`}
            >
              <span className="h-[5px] w-[5px] rounded-full bg-rose-500" />
              {formatWaitShort(patientReplyMinutes)}
            </span>
          ) : showBotAckChip ? (
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded-[4px] border border-amber-300 bg-amber-100 px-[7px] py-[3px] font-mono text-[11px] font-semibold leading-none text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/60 dark:text-amber-100"
              title="Unanswered bot reply"
              aria-label={`Unanswered bot reply, ${formatWaitShort(botAckMinutes)}`}
            >
              <span className="h-[5px] w-[5px] rounded-full bg-amber-500" />
              {formatWaitShort(botAckMinutes)}
            </span>
          ) : timeLabel ? (
            <span className="inline-flex shrink-0 items-center gap-1 font-mono text-[11px] leading-none text-muted-foreground">
              {isMutedChat ? (
                <BellOff className="h-3 w-3 shrink-0 text-muted-foreground/70" aria-label="Muted" />
              ) : null}
              {timeLabel}
            </span>
          ) : isMutedChat ? (
            <BellOff className="h-3 w-3 shrink-0 text-muted-foreground/70" aria-label="Muted" />
          ) : null}
        </div>
        <div className="mt-0.5 flex items-center justify-between gap-2">
          <p className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
            {typingThreadIds instanceof Set && tid && typingThreadIds.has(tid) ? (
              <span className="animate-pulse font-medium italic text-primary">typing…</span>
            ) : (
              <ThreadPreviewMarkdown text={getThreadPreview(thread)} />
            )}
          </p>
          <div className="flex shrink-0 items-center gap-1">
            {aiPaused && !isInternalScope ? <AiPausedChip locked={isLocked} iconOnly /> : null}
            {!isInternalScope ? <TaskCountBadge open={tasksOpen} total={tasksTotal} /> : null}
            {/* DISABLED (for now): the owner badge — thread assignment is hidden.
            {assignee && !isInternalScope ? (
              <ThreadOwnerBadge assignee={assignee} isMe={assignedToMe} />
            ) : null} */}
          </div>
        </div>
        {hasMetaRow ? (
          // Meta line: every item sits on the same 16px rhythm so chips and glyphs align.
          <div className="mt-1 flex h-4 items-center gap-1.5">
            {pendingUntilLabel ? (
              <span
                className="inline-flex h-4 shrink-0 items-center gap-1 text-[10px] font-medium leading-none text-amber-600 dark:text-amber-400"
                title={`Pending — returns ${pendingUntilLabel}, or sooner if the patient replies`}
              >
                {/* size-2! beats the row Button's [&_svg]:size-4 descendant override. */}
                <Clock className="size-2! shrink-0" strokeWidth={2.25} aria-hidden />
                <span className="font-mono">{pendingUntilLabel}</span>
              </span>
            ) : null}
            {lockReason ? (
              <span
                className="inline-flex h-4 min-w-0 shrink items-center gap-1 text-[10px] font-medium leading-none text-violet-500 dark:text-violet-400"
                title={`Locked · ${humanizeLockReason(lockReason)}`}
              >
                <Lock className="size-2! shrink-0" strokeWidth={2.25} aria-hidden />
                <span className="truncate font-mono">{humanizeLockReason(lockReason)}</span>
              </span>
            ) : null}
            {patientTags.length > 0 ? <PatientTagStrip tags={patientTags} max={3} /> : null}
            <span className="min-w-0 flex-1" />
            {showWaGroupCountChip ? (
              <span
                className="inline-flex h-4 shrink-0 items-center gap-1 rounded bg-green-500/10 px-1.5 text-[10px] font-medium leading-none text-green-700 dark:text-green-300"
                title={`WhatsApp group · ${waGroupParticipantCount} participant${waGroupParticipantCount === 1 ? "" : "s"}`}
              >
                {/* size-2.5! beats the row Button's [&_svg]:size-4 descendant override. */}
                <Users className="size-2.5! shrink-0" strokeWidth={2.25} aria-hidden />
                {waGroupParticipantCount}
              </span>
            ) : null}
            {showNumberBadge ? (
              isMergedGroupRow ? (
                /* One WhatsApp group bridged through several clinic gateways. Each dot reuses
                   the same per-botId hue as that clinic's rail in the conversation pane and its
                   dot in the reply-from picker, so the row and the picker read as one system. */
                <span
                  className="inline-flex h-4 shrink-0 items-center gap-1 rounded bg-green-500/10 px-1.5 text-[10px] font-medium leading-none text-green-700 dark:text-green-300"
                  title={`On ${waGroupSources.length} clinic numbers \u2014 replies go out from the one you pick:\n${waGroupSources
                    .map((s) => s.label)
                    .join("\n")}`}
                >
                  <span className="flex shrink-0 items-center" aria-hidden>
                    {waGroupSources.slice(0, 4).map((s, i) => (
                      <span
                        key={s.botId || s.threadId}
                        style={tenantColorVars(s.botId)}
                        className={cn(
                          "h-[5px] w-[5px] rounded-full bg-(--seg-solid) ring-1 ring-background",
                          i > 0 && "-ml-[2px]",
                        )}
                      />
                    ))}
                  </span>
                  <span className="truncate">{waGroupSources.length} numbers</span>
                </span>
              ) : (
                <span
                  className="inline-flex h-4 max-w-[7rem] shrink-0 items-center gap-1 rounded bg-green-500/10 px-1.5 text-[10px] font-medium leading-none text-green-700 dark:text-green-300"
                  title={`On ${whatsappNumberLabel}. Replies go out from this number.`}
                >
                  <span className="h-[5px] w-[5px] shrink-0 rounded-full bg-green-500" aria-hidden />
                  <span className="truncate">{whatsappNumberLabel}</span>
                </span>
              )
            ) : null}
            {showGroupChip ? <ThreadGroupChip group={currentGroup} /> : null}
          </div>
        ) : null}
      </div>
    </Button>
  );

  // The Claim quick-action overlays the row (sibling, not nested — a button inside the row
  // button would be invalid markup) and reveals on hover, design 01-inbox.
  // DISABLED (for now): the Claim overlay — thread assignment is hidden; assignment
  // lives on tasks only. The wrapper stays so row layout is unchanged.
  const rowWithClaim = (
    <div className="group/row relative">
      {rowButton}
      {/* {canClaim ? (
        <ClaimButton
          onClick={() => onClaimThread(tid)}
          className="absolute bottom-2 right-3 z-10 opacity-0 shadow-sm transition-opacity focus-visible:opacity-100 group-hover/row:opacity-100"
        />
      ) : null} */}
    </div>
  );

  if (isOptimistic) {
    return <Fragment key={rowKey}>{rowWithClaim}</Fragment>;
  }

  return (
    <ContextMenu key={rowKey}>
      <ContextMenuTrigger asChild>{rowWithClaim}</ContextMenuTrigger>
      {/* ContextMenuContent owns the scroll via its built-in ScrollArea; bump the cap to 420px */}
      <ContextMenuContent
        className="w-72 p-0"
        viewportClassName="max-h-[min(var(--radix-context-menu-content-available-height,420px),420px)]"
      >
        <div className="space-y-2 p-2.5">
            <header className="flex items-center gap-2 border-b border-border/60 pb-1.5">
              <Avatar className="h-6 w-6 shrink-0">
                <AvatarImage src={thread?.avatarUrl} />
                <AvatarFallback className={cn("text-[9px] font-semibold", avatarColorClasses)}>
                  {getThreadInitials(avatarName)}
                </AvatarFallback>
              </Avatar>
              <p
                className={cn(
                  "min-w-0 flex-1 truncate text-xs font-semibold",
                  threadTitleIsPlaceholder ? "text-muted-foreground" : "text-foreground",
                )}
                title={threadTitle}
              >
                {threadTitle}
              </p>
            </header>

            {isInternalScope ? (
              <section className="space-y-1">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Chat
                </p>
                <div className="space-y-1">
                  {showUnreadBadge && onMarkThreadRead ? (
                    <button
                      type="button"
                      onClick={() => onMarkThreadRead(tid)}
                      className={CHAT_MENU_ROW_CLASS}
                    >
                      <MailOpen className="h-3 w-3 shrink-0" strokeWidth={2.25} />
                      <span className="truncate">Mark as read</span>
                    </button>
                  ) : null}
                  {!showUnreadBadge && onMarkThreadUnread ? (
                    <button
                      type="button"
                      onClick={() => onMarkThreadUnread(tid)}
                      className={CHAT_MENU_ROW_CLASS}
                    >
                      <Mail className="h-3 w-3 shrink-0" strokeWidth={2.25} />
                      <span className="truncate">Mark as unread</span>
                    </button>
                  ) : null}
                  {onToggleMute ? (
                    <button
                      type="button"
                      onClick={() => onToggleMute(tid)}
                      className={CHAT_MENU_ROW_CLASS}
                    >
                      {isMutedChat ? (
                        <Bell className="h-3 w-3 shrink-0" strokeWidth={2.25} />
                      ) : (
                        <BellOff className="h-3 w-3 shrink-0" strokeWidth={2.25} />
                      )}
                      <span className="truncate">
                        {isMutedChat ? "Unmute notifications" : "Mute notifications"}
                      </span>
                    </button>
                  ) : null}
                  {onLeaveThread && !isInternalDirectThread(thread) ? (
                    <button
                      type="button"
                      onClick={() => onLeaveThread(tid, threadTitle)}
                      className={CHAT_MENU_DESTRUCTIVE_ROW_CLASS}
                    >
                      <LogOut className="h-3 w-3 shrink-0" strokeWidth={2.25} />
                      <span className="truncate">Leave group</span>
                    </button>
                  ) : null}
                  {onDeleteChat ? (
                    <button
                      type="button"
                      onClick={() => onDeleteChat(tid, threadTitle)}
                      className={CHAT_MENU_DESTRUCTIVE_ROW_CLASS}
                    >
                      <Trash2 className="h-3 w-3 shrink-0" strokeWidth={2.25} />
                      <span className="truncate">Delete chat</span>
                    </button>
                  ) : null}
                </div>
              </section>
            ) : null}

            {/* Patient-triage bot controls belong to Messages only — staff chats have no bot. */}
            {!isInternalScope ? (
            <section className="space-y-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                AI handling
              </p>
              <div className="grid grid-cols-2 gap-1">
                {[...primaryFlagDefs, ...restFlagDefs].map((def) => {
                  const Icon = def.icon;
                  const active = flagSet.has(def.shortName);
                  return (
                    <button
                      key={def.shortName}
                      type="button"
                      role="switch"
                      aria-checked={active}
                      aria-label={def.displayName}
                      onClick={() => {
                        void onThreadFlagChange(tid, def.shortName, !active);
                      }}
                      className={cn(
                        "flex w-full items-center gap-1.5 rounded-md border px-2 py-1 text-left text-[10.5px] font-medium leading-none transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        active
                          ? def.chipClassName
                          : "border-transparent text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <Icon className="h-3 w-3 shrink-0" strokeWidth={2.25} />
                      <span className="truncate">{def.displayName}</span>
                    </button>
                  );
                })}
              </div>
            </section>
            ) : null}

            {/* Workflow status is patient-triage machinery — a group row has no patient to
                triage, so the section hides there like it does in Chats. */}
            {onStatusChange && !isInternalScope && !isWaGroup ? (
              <section className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Status
                  </p>
                  {String(thread?.metadata?.status || "").trim() ? (
                    <button
                      type="button"
                      onClick={() => {
                        void onStatusChange(tid, "");
                      }}
                      className="inline-flex items-center gap-0.5 rounded text-[10px] font-medium text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <X className="h-3 w-3" strokeWidth={2.5} />
                      Clear
                    </button>
                  ) : null}
                </div>
                <div className="grid grid-cols-4 gap-1">
                  {THREAD_STATUSES.map((status) => {
                    const def = THREAD_STATUS_CATALOG[status];
                    const Icon = def.icon;
                    const active = effectiveStatus === status;
                    return (
                      <button
                        key={status}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        aria-label={def.label}
                        title={def.columnTitle}
                        onClick={() => {
                          if (active) return;
                          void onStatusChange(tid, status);
                        }}
                        className={cn(
                          "flex w-full items-center justify-center gap-1 rounded-md border px-1 py-1 text-[10px] font-medium leading-none transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          active
                            ? def.chipClassName
                            : "border-transparent text-muted-foreground hover:bg-muted hover:text-foreground",
                        )}
                      >
                        <Icon className="h-3 w-3 shrink-0" strokeWidth={2.25} />
                        <span className="truncate">{def.label}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {onPatientTagToggle && !isInternalScope && !isWaGroup && String(thread?.patientUserId || "").trim() ? (
              <section className="space-y-1">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Patient tags
                </p>
                <div className="grid grid-cols-2 gap-1">
                  {PATIENT_TAG_CATALOG.map((def) => {
                    const Icon = def.icon;
                    const active = patientTags.includes(def.key);
                    return (
                      <button
                        key={def.key}
                        type="button"
                        role="switch"
                        aria-checked={active}
                        aria-label={def.label}
                        title={`${def.label} — ${def.description}`}
                        onClick={() => {
                          void onPatientTagToggle(tid, def.key, !active);
                        }}
                        className={cn(
                          "flex w-full items-center gap-1.5 rounded-md border px-2 py-1 text-left text-[10.5px] font-medium leading-none transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          active
                            ? def.chipClassName
                            : "border-transparent text-muted-foreground hover:bg-muted hover:text-foreground",
                        )}
                      >
                        <Icon className="h-3 w-3 shrink-0" strokeWidth={2.25} />
                        <span className="truncate">{def.label}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {onThreadGroupChange && !isInternalScope ? (
              <section className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Group
                  </p>
                  {currentGroup ? (
                    <button
                      type="button"
                      onClick={() => {
                        void onThreadGroupChange(tid, null);
                      }}
                      className="inline-flex items-center gap-0.5 rounded text-[10px] font-medium text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <X className="h-3 w-3" strokeWidth={2.5} />
                      Clear
                    </button>
                  ) : null}
                </div>
                {groups.length > 0 ? (
                  <div className="grid grid-cols-2 gap-1">
                      {groups.map((g) => {
                        const color = getGroupColor(g.colorId);
                        const active = currentGroupId === g.id;
                        return (
                          <button
                            key={g.id}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            aria-label={g.name}
                            onClick={() => {
                              if (active) return;
                              void onThreadGroupChange(tid, g.id);
                            }}
                            className={cn(
                              "flex w-full items-center gap-1.5 rounded-md border px-2 py-1 text-left text-[10.5px] font-medium leading-none transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                              active
                                ? color.chipClassName
                                : "border-transparent text-muted-foreground hover:bg-muted hover:text-foreground",
                            )}
                          >
                            <span
                              className={cn("h-2 w-2 shrink-0 rounded-full", color.dotClassName)}
                            />
                            <span className="min-w-0 flex-1 truncate">{g.name}</span>
                            {active ? (
                              <Check className="h-3 w-3 shrink-0" strokeWidth={3} />
                            ) : null}
                          </button>
                        );
                      })}
                  </div>
                ) : (
                  <p className="rounded-md border border-dashed border-border/60 px-2 py-1.5 text-[10.5px] text-muted-foreground">
                    No groups yet. Add some in Settings.
                  </p>
                )}
              </section>
            ) : null}

            {import.meta.env.DEV && onDeleteThread ? (
              <section className="space-y-1 border-t border-border/60 pt-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Dev tools
                </p>
                <button
                  type="button"
                  onClick={() => onDeleteThread(tid, threadTitle)}
                  className="flex w-full items-center gap-1.5 rounded-md border border-destructive/40 px-2 py-1 text-left text-[10.5px] font-medium leading-none text-destructive transition hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Trash2 className="h-3 w-3 shrink-0" strokeWidth={2.25} />
                  <span className="truncate">Delete thread + all messages</span>
                </button>
              </section>
            ) : null}
        </div>
      </ContextMenuContent>
    </ContextMenu>
  );
};

// Memoized so a parent re-render (WS thread update, 30s clock tick, selection change) only
// re-renders the rows whose props actually changed — not all N loaded rows.
export const ThreadListItem = memo(ThreadListItemImpl);
